/**
 * Pictures, between the server and the canvas.
 *
 * Two things live here and they are deliberately separate:
 *
 *   the registry  What the server told us about each asset, including a signed
 *                 URL. Signed URLs expire, so they are runtime state and are
 *                 never written into a document. The document stores an id.
 *
 *   the loader    The decoded HTMLImageElement Konva actually draws, kept once
 *                 per asset. Decoding a 20 MP photo takes long enough that
 *                 doing it per frame would be visible.
 */

import type { AssetRef } from './model/types';

export interface Asset {
  id: string;
  kind: 'image' | 'svg' | 'pdf';
  name: string;
  url: string;
  w: number;
  h: number;
  bytes: number;
  mime: string;
}

const registry = new Map<string, Asset>();
const images = new Map<string, HTMLImageElement>();
const loading = new Map<string, Promise<HTMLImageElement | null>>();
const listeners = new Set<() => void>();

export function registerAssets(assets: Asset[]): void {
  for (const asset of assets) registry.set(asset.id, asset);

  announce();
}

export function knownAsset(id: string): Asset | null {
  return registry.get(id) ?? null;
}

/** Every picture this design has, newest first, for the Uploads panel. */
export function allAssets(): Asset[] {
  return Array.from(registry.values()).filter((asset) => asset.kind === 'image').reverse();
}

export function forgetAsset(id: string): void {
  registry.delete(id);
  images.delete(id);
  loading.delete(id);
  announce();
}

/** What the document should store about an asset - an id, never a URL. */
export function toDocumentRef(asset: Asset): AssetRef {
  return { id: asset.id, kind: asset.kind, ref: asset.id, w: asset.w, h: asset.h, dpi: 0 };
}

/**
 * The decoded picture, or null while it is still arriving.
 *
 * Null means "not yet", and the canvas draws the placeholder frame in the
 * meantime rather than nothing - a picture box that disappears while its
 * contents load looks like the design lost it.
 */
export function imageFor(id: string): HTMLImageElement | null {
  const ready = images.get(id);

  if (ready) return ready;

  const asset = registry.get(id);

  if (!asset || loading.has(id)) return null;

  loading.set(id, load(asset));

  return null;
}

async function load(asset: Asset): Promise<HTMLImageElement | null> {
  try {
    const element = new Image();

    // Same-origin: the signed link is on this site, and the canvas has to stay
    // untainted so a thumbnail can be read back off it.
    element.crossOrigin = 'anonymous';
    element.src = asset.url;

    await element.decode();

    images.set(asset.id, element);
    announce();

    return element;
  } catch (error) {
    // An expired link is the likely cause, and it is recoverable - the editor
    // asks the server for fresh links when it next opens the design.
    console.error('[trimbox] picture failed to load', asset.id, error);

    return null;
  } finally {
    loading.delete(asset.id);
  }
}

export function onAssetsChanged(listener: () => void): () => void {
  listeners.add(listener);

  return () => listeners.delete(listener);
}

function announce() {
  for (const listener of listeners) listener();
}
